import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Observable, combineLatest, debounceTime, map, of, startWith, take } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { Action, ActionCreator } from '@ngrx/store'
import {
  clearSearchHistoryAction,
  getAddSearchHistoryAction,
} from 'libs/gb-dashboard/shared/data-access-search-history/src/lib/+state/search-history.actions'
import { SearchHistoryFacade } from 'libs/gb-dashboard/shared/data-access-search-history/src/lib/+state/search-history.facade'
@UntilDestroy()
@Component({
  selector: 'gb-search',
  templateUrl: './gb-search.component.html',
  styleUrls: ['./gb-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GbSearchComponent implements OnInit, OnChanges {
  @Input() label: string = 'SEARCH'
  @Input() entityName?: string = ''
  @Input() searchHistory$: Observable<string[]> = of([])
  @Input() isDialog: boolean = false
  @Input() enableQueryParams: boolean = true
  @Input() loaded$: Observable<boolean> = of(true)

  @Output() searchTermEmit = new EventEmitter<string>()

  @ViewChild('input') input!: ElementRef<HTMLInputElement>

  filteredSearchHistory$: Observable<string[]> = of([])

  searchFormControl = new FormControl('')
  searchFormControlValueChanges$ = this.searchFormControl.valueChanges.pipe(startWith(''))
  focus = false

  get searchValue() {
    return this.searchFormControl.value
  }

  constructor(
    private searchHistoryFacade: SearchHistoryFacade,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    if (!this.isDialog && this.enableQueryParams) this.checkQueryParams()
    this.searchFormControl.valueChanges.pipe(debounceTime(500), untilDestroyed(this)).subscribe((value) => {
      if (!this.isDialog && this.enableQueryParams) this.updateQueryParams()
      const searchTerm = value || ''
      this.searchTermEmit.emit(searchTerm)
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.filteredSearchHistory$ = combineLatest([this.searchHistory$, this.searchFormControlValueChanges$]).pipe(
      map(([searchHistories, searchFormValue]) => {
        const uniqueHistorySet = new Set()
        const uniqueHistories = searchHistories.filter((history) => {
          const lowerCaseHistory = history.toLowerCase()
          if (uniqueHistorySet.has(lowerCaseHistory)) {
            return false
          } else {
            uniqueHistorySet.add(lowerCaseHistory)
            return true
          }
        })
        uniqueHistories.reverse()
        if (!searchFormValue || searchFormValue.length === 0) return uniqueHistories.slice(0, 8)
        return uniqueHistories.filter((history) => history.toLowerCase().startsWith(searchFormValue.toLowerCase()))
      }),
    )
  }

  selectSearchHistory(searchHistory: string) {
    this.searchFormControl.setValue(searchHistory)
    this.searchTermEmit.emit(searchHistory)
  }

  saveSearchHistory() {
    if (!this.entityName) return
    const actionCreator: ActionCreator = getAddSearchHistoryAction(this.entityName)
    const action = actionCreator({ searchTerm: this.searchValue }) as Action

    this.searchHistoryFacade.dispatch(action)
  }

  toggleFocus() {
    this.focus ? this.onBlur() : this.onFocus()
  }

  onFocus() {
    this.input.nativeElement.focus()
    this.focus = true
  }

  onBlur() {
    if (this.entityName && this.searchValue) this.saveSearchHistory()
    this.focus = false
  }

  clearInput() {
    this.searchFormControl.reset()
  }

  getSearchHistoryLabel(searchHistory: string): string {
    if (!this.searchValue) {
      return `<span>${searchHistory}</span>`
    }

    const matchedPart = searchHistory.substring(0, this.searchValue.length)
    const remainingPart = searchHistory.substring(this.searchValue.length)

    return `<span class='highlight'>${matchedPart}</span><span>${remainingPart}</span>`
  }

  clearSearchHistory() {
    if (this.entityName) this.searchHistoryFacade.dispatch(clearSearchHistoryAction(this.entityName)())
  }

  updateQueryParams(): void {
    const currentParams = this.activatedRoute.snapshot.queryParams

    const newParams = {
      ...currentParams,
      search: this.searchValue ? this.searchValue : undefined,
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: newParams,
      queryParamsHandling: 'merge',
    })
  }

  checkQueryParams(): void {
    const currentParams = this.activatedRoute.snapshot.queryParams

    const searchVal = currentParams['search']

    if (searchVal) {
      this.searchFormControl.setValue(searchVal)
      setTimeout(() => {
        this.searchTermEmit.emit(searchVal)
        this.saveSearchHistory()
      }, 0)
    }
  }
}
